import { CellValueChangedEvent, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AgFns, GridOptionsExt, ISortModel, ProxAgFns, UtilDialogService, wrapBusy } from '@core';
import { EntityFns } from '@data';
import {
  Addon,
  Feature,
  ProgramAllowance,
  ProgramAllowanceAddonException,
  ProgramAllowanceFeatureException,
  ProgramAllowanceRemainderRule,
  ProgramAllowanceUserGroupMap,
  ProgramUserGroup,
  ProgramViolationRule,
} from '@models';
import { UtilFns } from '@utils';
import { Entity } from 'breeze-client';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { ProgramFrmComponent } from './program-frm.component';

@Component({
  selector: 'prox-program-allowances-sub',
  templateUrl: './program-allowances.sub.component.html',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      prox-program-allowances-sub {
        .p-tabpanels {
          padding: 0 !important;
          height: calc(100% - 40px);
        }

        .p-tabpanel {
          height: 100%;
        }

        p-tabView {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .p-tabview-panels {
          flex: 1;
          height: 100%;
        }
      }

      prox-program-allowances-sub {
        prox-field label {
          margin-left: 5px;
          font-weight: 600;
          font-size: 1rem;
        }
      }
    `,
  ],
})
export class ProgramAllowancesSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  paGridOptions: GridOptionsExt = {};
  programAllowances?: ProgramAllowance[];
  selectedPa?: ProgramAllowance;

  availablePaPugs: ProgramUserGroup[] = [];
  paPugGridOptions: GridOptionsExt = {};
  // Note: these will both use the pugs array from above to provide a list of ProgramUserGroups among which only those in the ProgramAllowanceUserGroup or the ProgramIssuanceUserGroup will be checked

  paFeatureGridOptions: GridOptionsExt = {};
  paAddonGridOptions: GridOptionsExt = {};
  // Note: this is used to provide a list of Features among which only those in the ProgramAllowanceFeatureException will be checked
  paFeatures!: Feature[];
  paAddons!: Addon[];

  programAllowanceRemainderRules!: ProgramAllowanceRemainderRule[];
  programViolationRules!: ProgramViolationRule[];

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
    public pngDialogService: DialogService,
    public toastr: ToastrService,
  ) {
    //
  }

  get program() {
    return this.parent.program;
  }

  async ngOnInit() {
    this.paGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPaGridReady,
      onRowSelected: this.onPaRowSelected,

      rowModelType: 'clientSide',
      getRowId: (params) => params.data.id,
    });

    this.paPugGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPaPugGridReady,
      onCellValueChanged: this.onPaAugCellValueChanged,

      rowModelType: 'clientSide',
      // params.data is ProgramUserGroup
      getRowId: (params) => params.data.id,
    });

    this.paFeatureGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPaFeatureGridReady,

      rowModelType: 'clientSide',
      // params.data is Feature
      getRowId: (params) => params.data.id,
    });

    this.paAddonGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPaAddonGridReady,

      rowModelType: 'clientSide',
      // params.data is Feature
      getRowId: (params) => params.data.id,
    });

    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  @wrapBusy
  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);

    this.programViolationRules = await this.dbQueryService.getAll(ProgramViolationRule);

    this.programAllowanceRemainderRules = await this.dbQueryService.getAll(ProgramAllowanceRemainderRule);

    const isLoaded = this.program.entityAspect.isNavigationPropertyLoaded('programAllowances');
    if (isLoaded && !forceRefresh) {
      this.programAllowances = this.program.programAllowances;
    } else {
      const programAllowances = await this.dbQueryService.getProgramAllowances(this.program.id);

      this.programAllowances = _.sortBy(programAllowances, (pa) => pa.name);

      this.program.entityAspect.markNavigationPropertyAsLoaded('programAllowances');
    }

    // needed to refresh product type tag dropdowns and editability
    this.onPaGridReady(this.paGridOptions);
    this.onPaPugGridReady();
    this.onPaFeatureGridReady();
    this.onPaAddonGridReady();

    await AgFns.refreshGrid(this.paGridOptions, this.programAllowances);
    // The bottom subgrids will populate when selected.

    AgFns.sizeColumnsToFit(this.paGridOptions);

    AgFns.selectDefaultRow(this.paGridOptions);
  }

  onPaGridReady(event: GridReadyEvent | GridOptionsExt) {
    const colDefs = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Allowance Names',
        field: 'name',
        editable: !this.parent.isReadOnly(),
        sortable: false,
        ...AgFns.flexWidth(200),
      },
      // { headerName: 'Rank', field: 'displayOrder', hide: true },
      {
        headerName: 'Allowance',
        field: 'allowanceAmt',
        sortable: false,
        editable: !this.parent.isReadOnly(),
        // ...AgFns.fixWidth('money'),
        ...AgFns.fixWidth(130),
      },
      {
        ...AgFns.createCheckboxProps('Allow Over-Purchase?', 'allowOverAllowancePurchases'),
        editable: !this.parent.isReadOnly(),
        sortable: false,
        ...AgFns.fixWidth(160),
      },
      {
        ...AgFns.createCheckboxProps('Is Freight Charged To Allowance?', 'isFreightChargedToAllowance'),
        editable: !this.parent.isReadOnly(),
        sortable: false,
        ...AgFns.flexWidth(80),
      },
      {
        headerName: 'Est. Freight Charge Rate',
        field: 'estimatedFreightChargeRate',
        editable: !this.parent.isReadOnly(),
        type: 'numericColumn',
        valueParser: (p) => UtilFns.parsePct(p.newValue),
        valueFormatter: (p) => UtilFns.fmtPct(p.value, 2),
        ...AgFns.flexWidth(70),
      },
      this.parent.ifEditable(ProxAgFns.getEntityDeleteColDef(this.onPaDelete.bind(this))),
    ];
    const sortModel: ISortModel = [];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  onPaPugGridReady(event?: GridReadyEvent) {
    const colDefs = [
      {
        headerName: 'Applies to Group',
        field: 'name',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(100),
      },
      {
        ...AgFns.createIncludedInListCheckBoxProps(
          ProgramAllowanceUserGroupMap,
          this.dbQueryService.uow,
          'Is Included?',
          (e) => {
            return this.selectedPa?.programAllowanceUserGroupMaps.find((x) => x.programUserGroupId == e.data.id) != null;
          },
          (e) => {
            return {
              programAllowanceId: this.selectedPa?.id,
              programUserGroupId: e.data.id,
            };
          },
          !this.parent.isReadOnly(),
        ),
        ...AgFns.fixWidth(100),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    AgFns.initGrid(event ?? this.paPugGridOptions, colDefs, sortModel);
  }

  onPaFeatureGridReady(event?: GridReadyEvent) {
    const colDefs = [
      {
        headerName: 'Product Feature',
        field: 'name',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(100),
      },
      {
        ...AgFns.createIncludedInListCheckBoxProps(
          ProgramAllowanceFeatureException,
          this.dbQueryService.uow,
          'Exclude Feature Charge From Allowance?',
          (e) => {
            return this.selectedPa?.programAllowanceFeatureExceptions.find((x) => x.featureId == e.data.id) != null;
          },
          (e) => {
            return {
              programAllowanceId: this.selectedPa?.id,
              featureId: e.data.id,
            };
          },
          !this.parent.isReadOnly(),
        ),
        ...AgFns.fixWidth(260),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];

    AgFns.initGrid(event ?? this.paFeatureGridOptions, colDefs, sortModel);
  }

  onPaAddonGridReady(event?: GridReadyEvent) {
    const colDefs = [
      {
        headerName: 'Add-On',
        field: 'nameAndLocation',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(100),
      },
      {
        ...AgFns.createIncludedInListCheckBoxProps(
          ProgramAllowanceAddonException,
          this.dbQueryService.uow,
          'Exclude Add-on Charge From Allowance?',
          (e) => {
            return this.selectedPa?.programAllowanceAddonExceptions.find((x) => x.addonId == e.data.id) != null;
          },
          (e) => {
            return {
              programAllowanceId: this.selectedPa?.id,
              addonId: e.data.id,
            };
          },
          !this.parent.isReadOnly(),
        ),
        ...AgFns.fixWidth(260),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'nameAndLocation', sort: 'asc' }];
    AgFns.initGrid(event ?? this.paAddonGridOptions, colDefs, sortModel);
  }

  @wrapBusy
  async onPaRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const pa = event.data as ProgramAllowance;
    if (pa == null) return;
    this.selectedPa = pa;
    if (pa.entityAspect.entityState.isDeleted()) {
      this.selectedPa = undefined;
      return;
    }

    // augs is list of augs that are already associated with this pa and those that have not yet been associated with any pa.
    await this.refreshAvailablePaAugs(pa);

    if (this.paFeatures == null && this.paAddons == null) {
      const [r0, r1] = await Promise.all([
        this.dbQueryService.getFeaturesByProgram(this.parent.program.id, true),
        this.dbQueryService.getAddonsByProgram(this.parent.program.id, true),
      ]);

      this.paFeatures = r0;
      this.paAddons = r1;
    }

    AgFns.refreshGrid(this.paFeatureGridOptions, this.paFeatures);
    AgFns.refreshGrid(this.paAddonGridOptions, this.paAddons);
  }

  async refreshAvailablePaAugs(pa?: ProgramAllowance) {
    if (pa == null) {
      this.availablePaPugs = [];
    } else {
      const usedPaAugIds = new Set(
        _.flatten(this.program.programAllowances.map((pa) => pa.programAllowanceUserGroupMaps.map((paug) => paug.programUserGroupId))),
      );
      // OLD Wrong code
      // const availableAugs = this.program.account.programUserGroups.filter((x) => !usedPaAugIds.has(x.id));

      const pugs = this.program.approvalTree.approvalTreeUserGroups
        .map((x) => x.programUserGroup)
        .filter((pug) => pug != null);
      const availableAugs = pugs.filter((x) => !usedPaAugIds.has(x.id));

      const currentPaAugs = pa.programAllowanceUserGroupMaps
        .map((x) => x.programUserGroup)
        .filter((pug) => pug != null);
      availableAugs.push(...currentPaAugs);
      this.availablePaPugs = availableAugs;
    }
    await AgFns.refreshGrid(this.paPugGridOptions, this.availablePaPugs, 'sizeToFit');
    return this.availablePaPugs;
  }

  @wrapBusy
  async onPaAdd() {
    if (this.programAllowances == null) return;
    if (this.program.approvalTree == null) {
      await this.dialogService.okDialog('Unable to add', 'A Proximity Organization must be specified before you can add an allowance');
      return;
    }

    const pa = this.dbSaveService.createEntity(ProgramAllowance, {
      programId: this.program.id,
      // estimatedFreightChargeRate:
    });
    pa.entityAspect.validateEntity();

    this.programAllowances?.push(pa);
    AgFns.refreshGrid(this.paGridOptions, this.programAllowances);
    await UtilFns.wait(0);
    AgFns.selectGridRowByKey(this.paGridOptions, (e) => e.id, pa.id, 'name');
  }

  @wrapBusy
  async onPaDelete(pa: ProgramAllowance) {
    if (!this.programAllowances) return;

    UtilFns.assertNonNull(pa, 'Program Allowance should have been found');
    const ok = await this.dialogService.askIfOk(
      'Confirm Delete',
      `You will delete this Program Allowance record along with all job order logs associated with it.  THIS IS NOT REVERSIBLE.  Are you sure?`,
    );
    if (!ok) return;
    const logs = await this.dbQueryService.getProgramAllowanceUserLogsByProgramAllowanceId(pa.id);
    logs.forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));

    pa.programAllowanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    pa.programAllowanceAddonExceptions.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    pa.programAllowanceFeatureExceptions.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));

    EntityFns.deleteOrDetach(pa.entityAspect);

    _.remove(this.programAllowances, pa);
    AgFns.refreshGrid(this.paGridOptions, this.programAllowances);
    AgFns.refreshGrid(this.paPugGridOptions, []);
    AgFns.refreshGrid(this.paFeatureGridOptions, []);
    AgFns.refreshGrid(this.paAddonGridOptions, []);
  }

  onPaAugCellValueChanged(event: CellValueChangedEvent) {
    if (!this.selectedPa) return;

    if (this.selectedPa.programAllowanceUserGroupMaps.length == 0) {
      this.toastr.warning('At least one user group must be selected.');
    }
  }

  onPaSelectAllGroups() {
    if (!this.selectedPa) return;
    for (const pug of this.availablePaPugs) {
      const exists = this.selectedPa.programAllowanceUserGroupMaps.find((x) => x.programUserGroupId === pug.id);
      if (!exists) {
        // Try to restore a deleted entity first, otherwise create a new one
        const restored = this.dbQueryService.uow.undoIfDeleted(ProgramAllowanceUserGroupMap, [this.selectedPa.id, pug.id]);
        if (!restored) {
          this.dbSaveService.createEntity(ProgramAllowanceUserGroupMap, {
            programAllowanceId: this.selectedPa.id,
            programUserGroupId: pug.id,
          });
        }
      }
    }
    this.paPugGridOptions.apiX?.refreshCells();
  }

  onPaDeselectAllGroups() {
    if (!this.selectedPa) return;
    this.selectedPa.programAllowanceUserGroupMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    this.paPugGridOptions.apiX?.refreshCells();
  }

  async markError(errEnt: Entity, propName?: string) {
    const ent = errEnt as ProgramAllowance;
    AgFns.selectGridRowByKey(this.paGridOptions, (e: ProgramAllowance) => e.id, ent.id, propName);
  }
}
